import './axios-augment';
import type { AxiosInstance } from 'axios';
import { TEMP_ID_PREFIX } from './constants';
import type { CacheOps } from './cache';
import type { MutationHandlers } from './mutations-offline';
import type { QueueOps } from './queue';
import type { RouteMatcher } from './routes';
import type { EntityType, SyncQueueItem } from './types';
import { isLocalNewer, nowIso } from './timestamps';

export function createSyncEngine(deps: {
  cacheOps: CacheOps;
  queueOps: QueueOps;
  mutations: MutationHandlers;
  matchRoute: RouteMatcher['matchRoute'];
  isOffline: () => boolean;
  invalidateQueryForUrl?: (
    url: string,
    params?: Record<string, unknown>,
    options?: { afterSync?: boolean },
  ) => void;
}) {
  const { cacheOps, queueOps, mutations, matchRoute, isOffline, invalidateQueryForUrl } = deps;
  const { deleteEntity, getEntity, markSynced, remapEntityId, setLastSyncAt, upsertEntity } =
    cacheOps;
  const {
    dequeue,
    getQueue,
    incrementRetries,
    resolveId,
    resolvePayload,
    resolveUrl,
    saveIdMapping,
    setSyncing,
  } = queueOps;

  let syncInProgress = false;

  async function syncNow(client: AxiosInstance): Promise<{ synced: number; failed: number }> {
    if (syncInProgress || isOffline()) {
      return { synced: 0, failed: 0 };
    }

    syncInProgress = true;
    setSyncing(true, null);

    let synced = 0;
    let failed = 0;

    try {
      const queue = await getQueue();

      for (const item of queue) {
        try {
          await replayItem(client, item);
          await dequeue(item.id);
          synced += 1;
        } catch (err) {
          failed += 1;
          await incrementRetries(item.id);
          const message = err instanceof Error ? err.message : 'Sync failed';
          setSyncing(true, message);
        }
      }

      await setLastSyncAt(nowIso());
      setSyncing(false, failed > 0 ? `${failed} изменений не синхронизировано` : null);
    } finally {
      syncInProgress = false;
    }

    return { synced, failed };
  }

  async function replayItem(client: AxiosInstance, item: SyncQueueItem): Promise<void> {
    const url = await resolveUrl(item.url);
    const data = await resolvePayload(item.data);

    if (item.method === 'PATCH' || item.method === 'DELETE') {
      const shouldSkip = await shouldSkipMutation(client, item, url);
      if (shouldSkip) return;
    }

    let response;
    const syncConfig = { skipOffline: true };
    if (item.method === 'POST') {
      response = await client.post(url, data, syncConfig);
    } else if (item.method === 'PATCH') {
      response = await client.patch(url, data, syncConfig);
    } else {
      response = await client.delete(url, syncConfig);
    }

    const responseData = response.data;
    const route = matchRoute(item.method, item.url);
    let syncedEntityId: string | undefined;

    if (item.method === 'POST' && responseData && typeof responseData === 'object') {
      const serverRecord = responseData as Record<string, unknown>;
      const serverId = String(serverRecord.id);
      const tempId = item.entityId ?? extractTempId(item);

      if (tempId?.startsWith(TEMP_ID_PREFIX) && serverId) {
        await saveIdMapping(tempId, serverId, item.entityType);
        await deleteEntity(item.entityType as EntityType, tempId);
        await remapEntityId(item.entityType as EntityType, tempId, serverId);
        syncedEntityId = serverId;
      } else if (serverId) {
        syncedEntityId = serverId;
      }
    }

    if (route && responseData !== undefined && item.method !== 'DELETE') {
      await mutations.cacheOnlineResponse(item.method, item.url, responseData, undefined, {
        force: true,
        requestData: data as Record<string, unknown> | undefined,
      });
    }

    if (syncedEntityId) {
      await markSynced(item.entityType as EntityType, syncedEntityId);
    } else if (item.entityId) {
      await markSynced(item.entityType as EntityType, await resolveId(item.entityId));
    }

    invalidateQueryForUrl?.(item.url, undefined, { afterSync: true });
  }

  function extractTempId(item: SyncQueueItem): string | undefined {
    if (item.entityId?.startsWith(TEMP_ID_PREFIX)) return item.entityId;
    const body = item.data as Record<string, unknown> | undefined;
    if (body?.id && typeof body.id === 'string' && body.id.startsWith(TEMP_ID_PREFIX)) {
      return body.id;
    }
    return undefined;
  }

  async function shouldSkipMutation(
    client: AxiosInstance,
    item: SyncQueueItem,
    resolvedUrl: string,
  ): Promise<boolean> {
    if (!item.entityId || item.entityId.startsWith(TEMP_ID_PREFIX)) {
      return false;
    }

    const local = await getEntity(item.entityType as EntityType, item.entityId);
    if (local?._pendingSync) return false;
    if (!local?._localUpdatedAt) return false;

    try {
      const getRoute = matchRoute('GET', item.url.replace(/\/[^/]+$/, `/${item.entityId}`));
      let serverRecord: Record<string, unknown> | null = null;

      if (item.entityType === 'trainerProfile') {
        const res = await client.get('/trainers/me', { skipOffline: true });
        serverRecord = res.data as Record<string, unknown>;
      } else if (item.entityType === 'clientCoins') {
        return false;
      } else if (item.entityType === 'measurements') {
        const localMeasurement = local as Record<string, unknown>;
        const clientId = localMeasurement.clientId as string | undefined;
        if (clientId) {
          const res = await client.get(`/measurements/${clientId}`, { skipOffline: true });
          const list = res.data as Record<string, unknown>[];
          serverRecord = list.find((entry) => String(entry.id) === item.entityId) ?? null;
        }
      } else {
        const getUrl = buildGetUrl(item);
        if (getUrl) {
          const res = await client.get(getUrl, { skipOffline: true });
          serverRecord = res.data as Record<string, unknown>;
        }
      }

      if (serverRecord && !isLocalNewer(String(local._localUpdatedAt), serverRecord)) {
        await upsertEntity(item.entityType as EntityType, serverRecord, false, { force: true });
        void getRoute;
        void resolvedUrl;
        return true;
      }
    } catch {
      return false;
    }

    void resolvedUrl;
    return false;
  }

  function buildGetUrl(item: SyncQueueItem): string | null {
    const id = item.entityId;
    if (!id) return null;

    switch (item.entityType) {
      case 'clients':
        return `/clients/${id}`;
      case 'bookings':
        return `/bookings/${id}`;
      case 'tasks':
        return `/tasks/${id}`;
      case 'leads':
        return `/leads/${id}`;
      case 'packages': {
        const match = item.url.match(/^\/clients\/([^/]+)\/packages/);
        return match ? `/clients/${match[1]}/packages/${id}` : null;
      }
      case 'challenges':
        return `/challenges/${id}`;
      case 'trainingPlans':
        return `/training-plans/${id}`;
      default:
        return null;
    }
  }

  return { syncNow };
}
